import React, { useState, useRef, useMemo, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import AppHeader from '../components/ui/AppHeader';
import { Plus, Upload, Edit2, Trash2, Database, ChevronDown, ChevronRight, X, FileText, AlertCircle, CheckSquare, Square, ArrowUp, ArrowDown, ArrowUpDown, Search, BarChart3, Flag, History } from 'lucide-react';
import SupplierCombobox from '../components/ui/SupplierCombobox';
import SupplierImportModal from '../components/ui/SupplierImportModal';
import TimberReport from '../components/ui/TimberReport';
import MaterialsPriceUpdateReviewPanel from '../components/materials/MaterialsPriceUpdateReviewPanel';
import MaterialsCatalogSnapshotsModal from '../components/materials/MaterialsCatalogSnapshotsModal';
import TimberSupplierPriceTrend from '../components/materials/TimberSupplierPriceTrend';
import HardwareSupplierPriceTrend from '../components/materials/HardwareSupplierPriceTrend';
import {
    TimberItem,
    TimberMoistureContent,
    SupplierPrice,
    subscribeToTimberItems,
    importTimberItems,
    addOrMergeTimberItem,
    updateTimberItem,
    deleteTimberItem,
    bulkUpdateTimberItems,
    bulkDeleteTimberItems,
    TimberItemInput,
    HardwareItem,
    HardwareItemInput,
    HardwareCategory,
    HARDWARE_CATEGORIES,
    subscribeToHardwareItems,
    addHardwareItem,
    updateHardwareItem,
    deleteHardwareItem,
    deduplicateSupplierPricing,
    mergeSupplierPriceEntry,
    GlulamItem,
    GlulamItemInput,
    subscribeToGlulamItems,
    importGlulamItems,
    addOrMergeGlulamItem,
    updateGlulamItem,
    deleteGlulamItem,
    bulkUpdateGlulamItems,
    bulkDeleteGlulamItems,
} from '../utils/materialsDatabaseService';
import { repairRecentTimberPriceHistory } from '../utils/materialsPriceUpdateService';
import {
    formatHardwareDiameterLengthLabel,
    resolveHardwareDiameterLengthForDisplay,
    resolveHardwareUnitPrice,
} from '../utils/hardwareCatalogDisplay';
import './MaterialsDatabasePage.css';

const GRADE_OPTIONS = ['#1/BTR', '#2/BTR', '#1', '#2', 'Select', 'FAS', 'Standard', 'Construction', 'Utility'];

const GLULAM_GRADE_OPTIONS = ['24F-V4', '24F-EWS', '24F-EX', '20F-EWS', '20F-V4', 'Other'];

type MaterialsTab = 'timber' | 'glulam' | 'hardware';

type WoodPricingItem = TimberItem | GlulamItem;

function formatMoistureLabel(m?: TimberMoistureContent): string {
    if (m === 'green') return 'Green';
    if (m === 'kiln_dried') return 'Kiln dried';
    return '—';
}

function moistureSortKey(m?: TimberMoistureContent): number {
    if (m === 'green') return 1;
    if (m === 'kiln_dried') return 2;
    return 0;
}

// Parse dimensions (HxWxL where H,W in inches, L in feet) to board feet
const parseDimensions = (dimensions: string): number => {
    const match = dimensions.match(/(\d+\.?\d*)\s*[xX×]\s*(\d+\.?\d*)\s*[xX×]\s*(\d+\.?\d*)/);
    if (!match) return 0;
    const height = parseFloat(match[1]);
    const width = parseFloat(match[2]);
    const length = parseFloat(match[3]);
    return (height * width * length) / 12;
};

// Parse dimensions into parts for sorting
const parseDimensionParts = (dimensions: string): { height: number; width: number; length: number } => {
    const match = dimensions.match(/(\d+\.?\d*)\s*[xX×]\s*(\d+\.?\d*)\s*[xX×]\s*(\d+\.?\d*)/);
    if (!match) return { height: 0, width: 0, length: 0 };
    return {
        height: parseFloat(match[1]) || 0,
        width: parseFloat(match[2]) || 0,
        length: parseFloat(match[3]) || 0,
    };
};

// Normalize dimension string for search (e.g. "10 x 10 x 24" and "10x10x24" both become "10x10x24")
const normalizeDimensionsForSearch = (s: string): string =>
    s.replace(/\s*[xX×]\s*/g, 'x').replace(/\s+/g, ' ').trim().toLowerCase();

// Check if a timber has an odd (non-even) width or height
const hasOddDimension = (dimensions: string): boolean => {
    const parts = parseDimensionParts(dimensions);
    if (parts.height === 0 && parts.width === 0) return false;
    return parts.height % 2 !== 0 || parts.width % 2 !== 0;
};

// Sort config types
type SortField = 'grade' | 'moisture' | 'dimensions' | 'boardFeet' | 'piecePrice' | 'costPerBdFt';
type SortDirection = 'asc' | 'desc';
type DimensionSortOrder = 'height' | 'width' | 'length';
interface SortConfig {
    field: SortField | null;
    direction: SortDirection;
    dimensionOrder: DimensionSortOrder[];
}

const MaterialsDatabasePage: React.FC = () => {
    const [searchParams] = useSearchParams();
    const [activeTab, setActiveTab] = useState<MaterialsTab>(() => {
        const tab = (searchParams.get('tab') || '').toLowerCase();
        if (tab === 'hardware' || tab === 'glulam' || tab === 'timber') return tab;
        // Legacy deep link for price reviews → hardware (timber/glulam auto-apply now).
        if (tab === 'price-reviews' || searchParams.get('reviews') === '1') return 'hardware';
        return 'timber';
    });
    const [timberItems, setTimberItems] = useState<TimberItem[]>([]);
    const [glulamItems, setGlulamItems] = useState<GlulamItem[]>([]);
    const [glulamLoading, setGlulamLoading] = useState(true);
    const [hardwareItems, setHardwareItems] = useState<HardwareItem[]>([]);
    const [selectedCategory, setSelectedCategory] = useState<HardwareCategory | 'all'>('all');
    const [loading, setLoading] = useState(true);
    const [expandedSpecies, setExpandedSpecies] = useState<Set<string>>(new Set());
    const [expandedSuppliers, setExpandedSuppliers] = useState<Set<string>>(new Set()); // item IDs with expanded supplier rows
    const [expandedHardwareSuppliers, setExpandedHardwareSuppliers] = useState<Set<string>>(() => new Set());
    const [supplierDropdownOpen, setSupplierDropdownOpen] = useState<string | null>(null); // item ID when dropdown is open
    const [editingSupplierEntry, setEditingSupplierEntry] = useState<{ itemId: string; spIdx: number; piecePrice: number; costPerBdFt: number } | null>(null);
    const supplierDropdownRef = useRef<HTMLDivElement>(null);
    const [showAddDropdown, setShowAddDropdown] = useState(false);
    const [showTimberReport, setShowTimberReport] = useState(false);
    const [showSnapshotsModal, setShowSnapshotsModal] = useState(false);

    // Import modal state
    const [showImportModal, setShowImportModal] = useState(false);
    const [showSupplierImportModal, setShowSupplierImportModal] = useState(false);
    const [importSpecies, setImportSpecies] = useState('');
    const [importGrade, setImportGrade] = useState('#1/BTR');
    const [importMoisture, setImportMoisture] = useState<TimberMoistureContent>('');
    const [csvText, setCsvText] = useState('');
    const [importPreview, setImportPreview] = useState<TimberItemInput[]>([]);
    const [importError, setImportError] = useState('');
    const [importing, setImporting] = useState(false);
    const [importSupplier, setImportSupplier] = useState('');
    const fileInputRef = useRef<HTMLInputElement>(null);

    // Bulk edit state
    const [bulkEditMode, setBulkEditMode] = useState(false);
    const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
    const [showBulkEditModal, setShowBulkEditModal] = useState(false);
    const [bulkEditSpecies, setBulkEditSpecies] = useState('');
    const [bulkEditGrade, setBulkEditGrade] = useState('');
    const [bulkEditCostPerBdFt, setBulkEditCostPerBdFt] = useState('');
    const [bulkEditSupplier, setBulkEditSupplier] = useState('');
    /** '' = leave dryness unchanged; 'unspecified' | 'green' | 'kiln_dried' = set on all selected */
    const [bulkEditMoisture, setBulkEditMoisture] = useState('');
    const [bulkUpdating, setBulkUpdating] = useState(false);

    // Add/Edit modal state
    const [showItemModal, setShowItemModal] = useState(false);
    const [editingItem, setEditingItem] = useState<TimberItem | null>(null);
    const [editingGlulamItem, setEditingGlulamItem] = useState<GlulamItem | null>(null);
    const [editingHardwareItem, setEditingHardwareItem] = useState<HardwareItem | null>(null);
    const [itemForm, setItemForm] = useState<{
        species: string;
        grade: string;
        dimensions: string;
        moistureContent: TimberMoistureContent;
        piecePrice: number;
        costPerBdFt: number;
        boardFeet: number;
        supplier: string;
    }>({
        species: '',
        grade: '#1/BTR',
        dimensions: '',
        moistureContent: '',
        piecePrice: 0,
        costPerBdFt: 0,
        boardFeet: 0,
        supplier: '',
    });
    const [hardwareForm, setHardwareForm] = useState({
        category: 'Fasteners' as HardwareCategory,
        name: '',
        description: '',
        unitPrice: 0,
        unit: 'each',
        widthInches: undefined as number | undefined,
        sku: '',
        supplier: '',
    });

    // Sort state
    const [sortConfig, setSortConfig] = useState<SortConfig>({
        field: null,
        direction: 'asc',
        dimensionOrder: ['height', 'width', 'length'],
    });

    // Search state
    const [searchQuery, setSearchQuery] = useState('');

    // Compute unique supplier names for combobox suggestions (timber + hardware)
    const supplierNames = useMemo(() =>
        Array.from(new Set([
            ...timberItems.map(i => i.supplier).filter(Boolean) as string[],
            ...glulamItems.map(i => i.supplier).filter(Boolean) as string[],
            ...hardwareItems.map(i => i.supplier).filter(Boolean) as string[],
            'Rothoblaas',
        ])).sort()
        , [timberItems, glulamItems, hardwareItems]);

    const isWoodTab = activeTab === 'timber' || activeTab === 'glulam';
    const woodLoading = activeTab === 'glulam' ? glulamLoading : loading;

    // Firebase subscription - Timber
    useEffect(() => {
        setLoading(true);
        const unsubscribe = subscribeToTimberItems(
            (items) => {
                setTimberItems(items);
                setLoading(false);
                // Auto-expand all species on first load
                if (expandedSpecies.size === 0) {
                    const species = new Set(items.map(i => i.species));
                    setExpandedSpecies(species);
                }
            },
            (error) => {
                console.error('Error loading timber items:', error);
                setLoading(false);
            }
        );
        return () => unsubscribe();
    }, []);

    useEffect(() => {
        setGlulamLoading(true);
        const unsubscribe = subscribeToGlulamItems(
            (items) => {
                setGlulamItems(items);
                setGlulamLoading(false);
                if (expandedSpecies.size === 0) {
                    const species = new Set(items.map((i) => i.species));
                    setExpandedSpecies(species);
                }
            },
            (error) => {
                console.error('Error loading glulam items:', error);
                setGlulamLoading(false);
            },
        );
        return () => unsubscribe();
    }, []);

    // One-shot: backfill / fix Trend history dates from proposal createdAt → reviewedAt.
    // Also merge grade-casing duplicates (#2/BTR vs #2/Btr) so proposal pricing matches.
    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                await deduplicateSupplierPricing();
            } catch (err) {
                if (!cancelled) {
                    console.warn('[MaterialsDatabase] grade/supplier dedupe skipped', err);
                }
            }
            try {
                // force: rewrite same-day stamps from the earlier repair pass
                await repairRecentTimberPriceHistory(5, { force: true });
            } catch (err) {
                if (!cancelled) {
                    console.warn('[MaterialsDatabase] price history repair skipped', err);
                }
            }
        })();
        return () => {
            cancelled = true;
        };
    }, []);

    // Close add-item dropdown on outside click
    useEffect(() => {
        if (!showAddDropdown) return;
        const handler = (e: MouseEvent) => {
            const target = e.target as HTMLElement;
            if (!target.closest('.add-item-dropdown-wrapper')) {
                setShowAddDropdown(false);
            }
        };
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, [showAddDropdown]);

    // Firebase subscription - Hardware
    useEffect(() => {
        const unsubscribe = subscribeToHardwareItems(
            (items) => {
                setHardwareItems(items);
            },
            (error) => {
                console.error('Error loading hardware items:', error);
            }
        );
        return () => unsubscribe();
    }, []);

    // Group and sort timber by species (with search filter)
    const groupedTimber = useMemo(() => {
        const groups: Record<string, TimberItem[]> = {};

        // Filter by search query
        const searchLower = searchQuery.toLowerCase().trim();
        const searchNorm = normalizeDimensionsForSearch(searchQuery);
        const filteredItems = searchLower
            ? timberItems.filter(item =>
                item.species.toLowerCase().includes(searchLower) ||
                item.grade.toLowerCase().includes(searchLower) ||
                formatMoistureLabel(item.moistureContent).toLowerCase().includes(searchLower) ||
                normalizeDimensionsForSearch(item.dimensions).includes(searchNorm) ||
                (item.supplier || '').toLowerCase().includes(searchLower)
            )
            : timberItems;

        filteredItems.forEach(item => {
            if (!groups[item.species]) {
                groups[item.species] = [];
            }
            groups[item.species].push(item);
        });

        // Sort items within each group if sort is active
        if (sortConfig.field) {
            Object.keys(groups).forEach(species => {
                groups[species].sort((a, b) => {
                    let comparison = 0;

                    switch (sortConfig.field) {
                        case 'grade':
                            comparison = a.grade.localeCompare(b.grade);
                            break;
                        case 'moisture':
                            comparison = moistureSortKey(a.moistureContent) - moistureSortKey(b.moistureContent);
                            if (comparison === 0) {
                                comparison = formatMoistureLabel(a.moistureContent).localeCompare(formatMoistureLabel(b.moistureContent));
                            }
                            break;
                        case 'dimensions': {
                            const aParts = parseDimensionParts(a.dimensions);
                            const bParts = parseDimensionParts(b.dimensions);
                            for (const dim of sortConfig.dimensionOrder) {
                                comparison = aParts[dim] - bParts[dim];
                                if (comparison !== 0) break;
                            }
                            break;
                        }
                        case 'boardFeet':
                            comparison = a.boardFeet - b.boardFeet;
                            break;
                        case 'piecePrice':
                            comparison = a.piecePrice - b.piecePrice;
                            break;
                        case 'costPerBdFt':
                            comparison = a.costPerBdFt - b.costPerBdFt;
                            break;
                    }

                    return sortConfig.direction === 'asc' ? comparison : -comparison;
                });
            });
        }

        return groups;
    }, [timberItems, sortConfig, searchQuery]);

    const groupedGlulam = useMemo(() => {
        const groups: Record<string, GlulamItem[]> = {};
        const searchLower = searchQuery.toLowerCase().trim();
        const searchNorm = normalizeDimensionsForSearch(searchQuery);
        const filteredItems = searchLower
            ? glulamItems.filter((item) =>
                item.species.toLowerCase().includes(searchLower) ||
                item.grade.toLowerCase().includes(searchLower) ||
                normalizeDimensionsForSearch(item.dimensions).includes(searchNorm) ||
                (item.supplier || '').toLowerCase().includes(searchLower),
            )
            : glulamItems;

        filteredItems.forEach((item) => {
            if (!groups[item.species]) groups[item.species] = [];
            groups[item.species].push(item);
        });

        if (sortConfig.field && sortConfig.field !== 'moisture') {
            Object.keys(groups).forEach((species) => {
                groups[species].sort((a, b) => {
                    let comparison = 0;
                    switch (sortConfig.field) {
                        case 'grade':
                            comparison = a.grade.localeCompare(b.grade);
                            break;
                        case 'dimensions': {
                            const aParts = parseDimensionParts(a.dimensions);
                            const bParts = parseDimensionParts(b.dimensions);
                            for (const dim of sortConfig.dimensionOrder) {
                                comparison = aParts[dim] - bParts[dim];
                                if (comparison !== 0) break;
                            }
                            break;
                        }
                        case 'boardFeet':
                            comparison = a.boardFeet - b.boardFeet;
                            break;
                        case 'piecePrice':
                            comparison = a.piecePrice - b.piecePrice;
                            break;
                        case 'costPerBdFt':
                            comparison = a.costPerBdFt - b.costPerBdFt;
                            break;
                        default:
                            break;
                    }
                    return sortConfig.direction === 'asc' ? comparison : -comparison;
                });
            });
        }

        return groups;
    }, [glulamItems, sortConfig, searchQuery]);

    const activeGroupedWood = activeTab === 'glulam' ? groupedGlulam : groupedTimber;

    const persistWoodItem = useCallback(
        async (id: string, updates: Partial<TimberItemInput> | Partial<GlulamItemInput>) => {
            if (activeTab === 'glulam') {
                await updateGlulamItem(id, updates as Partial<GlulamItemInput>);
            } else {
                await updateTimberItem(id, updates as Partial<TimberItemInput>);
            }
        },
        [activeTab],
    );

    // Sort handler
    const handleSort = (field: SortField) => {
        setSortConfig(prev => {
            if (prev.field === field) {
                // Toggle direction or clear
                if (prev.direction === 'asc') {
                    return { ...prev, direction: 'desc' };
                } else {
                    return { ...prev, field: null, direction: 'asc' };
                }
            } else {
                return { ...prev, field, direction: 'asc' };
            }
        });
    };

    // Cycle dimension sort order
    const cycleDimensionOrder = () => {
        const orders: DimensionSortOrder[][] = [
            ['height', 'width', 'length'],
            ['width', 'height', 'length'],
            ['length', 'height', 'width'],
            ['height', 'length', 'width'],
        ];
        const currentIndex = orders.findIndex(
            o => JSON.stringify(o) === JSON.stringify(sortConfig.dimensionOrder)
        );
        const nextIndex = (currentIndex + 1) % orders.length;
        setSortConfig(prev => ({ ...prev, dimensionOrder: orders[nextIndex] }));
    };

    const getSortIcon = (field: SortField) => {
        if (sortConfig.field !== field) return <ArrowUpDown size={14} />;
        return sortConfig.direction === 'asc' ? <ArrowUp size={14} /> : <ArrowDown size={14} />;
    };

    const toggleSpecies = (species: string) => {
        const next = new Set(expandedSpecies);
        if (next.has(species)) {
            next.delete(species);
        } else {
            next.add(species);
        }
        setExpandedSpecies(next);
    };

    const handleAdd = () => {
        if (activeTab === 'timber' || activeTab === 'glulam') {
            setEditingItem(null);
            setEditingGlulamItem(null);
            setItemForm({
                species: '',
                grade: activeTab === 'glulam' ? '24F-V4' : '#1/BTR',
                dimensions: '',
                moistureContent: '',
                piecePrice: 0,
                costPerBdFt: 0,
                boardFeet: 0,
                supplier: '',
            });
        } else {
            setEditingHardwareItem(null);
            setHardwareForm({
                category: 'Fasteners',
                name: '',
                description: '',
                unitPrice: 0,
                unit: 'each',
                widthInches: undefined,
                sku: '',
                supplier: '',
            });
        }
        setShowItemModal(true);
    };

    const handleEdit = (id: string) => {
        const timber = timberItems.find(i => i.id === id);
        if (timber) {
            setEditingItem(timber);
            setEditingGlulamItem(null);
            setItemForm({
                species: timber.species,
                grade: timber.grade,
                dimensions: timber.dimensions,
                moistureContent: timber.moistureContent ?? '',
                piecePrice: timber.piecePrice,
                costPerBdFt: timber.costPerBdFt,
                boardFeet: timber.boardFeet,
                supplier: timber.supplier || '',
            });
            setShowItemModal(true);
            return;
        }
        const glulam = glulamItems.find((i) => i.id === id);
        if (glulam) {
            setEditingGlulamItem(glulam);
            setEditingItem(null);
            setItemForm({
                species: glulam.species,
                grade: glulam.grade,
                dimensions: glulam.dimensions,
                moistureContent: '',
                piecePrice: glulam.piecePrice,
                costPerBdFt: glulam.costPerBdFt,
                boardFeet: glulam.boardFeet,
                supplier: glulam.supplier || '',
            });
            setShowItemModal(true);
            return;
        }
        const hwItem = hardwareItems.find(i => i.id === id);
        if (hwItem) {
            setEditingHardwareItem(hwItem);
            setHardwareForm({
                category: hwItem.category,
                name: hwItem.name,
                description: hwItem.description,
                unitPrice: hwItem.unitPrice,
                unit: hwItem.unit,
                widthInches: hwItem.widthInches,
                sku: hwItem.sku || '',
                supplier: hwItem.supplier || '',
            });
            setShowItemModal(true);
        }
    };

    const handleSaveItem = async () => {
        try {
            if (activeTab === 'timber') {
                if (editingItem) {
                    await updateTimberItem(editingItem.id, itemForm);
                } else {
                    await addOrMergeTimberItem(itemForm);
                }
            } else if (activeTab === 'glulam') {
                const glulamPayload: GlulamItemInput = {
                    species: itemForm.species,
                    grade: itemForm.grade,
                    dimensions: itemForm.dimensions,
                    piecePrice: itemForm.piecePrice,
                    costPerBdFt: itemForm.costPerBdFt,
                    boardFeet: itemForm.boardFeet,
                    supplier: itemForm.supplier,
                };
                if (editingGlulamItem) {
                    await updateGlulamItem(editingGlulamItem.id, glulamPayload);
                } else {
                    await addOrMergeGlulamItem(glulamPayload);
                }
            } else {
                if (editingHardwareItem) {
                    await updateHardwareItem(editingHardwareItem.id, hardwareForm);
                } else {
                    await addHardwareItem(hardwareForm);
                }
            }
            setShowItemModal(false);
            setEditingItem(null);
            setEditingGlulamItem(null);
            setEditingHardwareItem(null);
        } catch (error) {
            console.error('Error saving item:', error);
            alert('Failed to save item');
        }
    };

    const handleDelete = async (id: string) => {
        if (!window.confirm('Are you sure you want to delete this item?')) return;
        if (activeTab === 'glulam') await deleteGlulamItem(id);
        else await deleteTimberItem(id);
    };

    const formatCurrency = (val: number) => {
        return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(val);
    };

    const formatHardwarePrice = (item: HardwareItem) => {
        const price = resolveHardwareUnitPrice(item);
        if (price == null) return '—';
        return formatCurrency(price);
    };

    /** Group hardware by primary supplier (mirrors timber species groups). */
    const groupedHardware = useMemo(() => {
        const filtered = hardwareItems.filter(
            (item) => selectedCategory === 'all' || item.category === selectedCategory,
        );
        const groups: Record<string, HardwareItem[]> = {};
        for (const item of filtered) {
            const key = (item.supplier || 'Unassigned').trim() || 'Unassigned';
            if (!groups[key]) groups[key] = [];
            groups[key].push(item);
        }
        // Within each supplier: Fasteners by Ø×L ascending, then SKU / name.
        for (const key of Object.keys(groups)) {
            groups[key].sort((a, b) => {
                const da = resolveHardwareDiameterLengthForDisplay(
                    a.category,
                    a.name,
                    a.description,
                    a.sku,
                );
                const db = resolveHardwareDiameterLengthForDisplay(
                    b.category,
                    b.name,
                    b.description,
                    b.sku,
                );
                if (da && db) {
                    if (da.diameter !== db.diameter) return da.diameter - db.diameter;
                    if (da.length !== db.length) return da.length - db.length;
                } else if (da && !db) return -1;
                else if (!da && db) return 1;
                return (a.sku || a.name || '').localeCompare(b.sku || b.name || '', undefined, {
                    sensitivity: 'base',
                });
            });
        }
        return Object.fromEntries(
            Object.entries(groups).sort(([a], [b]) => a.localeCompare(b, undefined, { sensitivity: 'base' })),
        );
    }, [hardwareItems, selectedCategory]);

    useEffect(() => {
        // Expand first supplier group by default when data loads / category changes.
        const keys = Object.keys(groupedHardware);
        if (!keys.length) return;
        setExpandedHardwareSuppliers((prev) => {
            if (prev.size > 0) {
                const next = new Set(Array.from(prev).filter((k) => keys.includes(k)));
                if (next.size > 0) return next;
            }
            return new Set([keys[0]]);
        });
    }, [groupedHardware]);

    const toggleHardwareSupplier = (supplier: string) => {
        setExpandedHardwareSuppliers((prev) => {
            const next = new Set(prev);
            if (next.has(supplier)) next.delete(supplier);
            else next.add(supplier);
            return next;
        });
    };

    // Delete a single supplier pricing entry
    const handleDeleteSupplierEntry = async (item: WoodPricingItem, spIdx: number) => {
        const sp = item.supplierPricing?.[spIdx];
        if (!sp || !window.confirm(`Remove ${sp.supplier} pricing from this item?`)) return;
        const updatedPricing = (item.supplierPricing || []).filter((_: any, i: number) => i !== spIdx);
        const updates: any = { supplierPricing: updatedPricing };
        // If we deleted the active supplier, clear top-level supplier
        if (sp.supplier === item.supplier) {
            if (updatedPricing.length > 0) {
                updates.supplier = updatedPricing[0].supplier;
                updates.piecePrice = updatedPricing[0].piecePrice;
                updates.costPerBdFt = updatedPricing[0].costPerBdFt;
            } else {
                updates.supplier = '';
                updates.piecePrice = 0;
                updates.costPerBdFt = 0;
            }
        }
        await persistWoodItem(item.id, updates);
    };

    // Save an edited supplier pricing entry
    const handleSaveSupplierEntry = async (item: WoodPricingItem) => {
        if (!editingSupplierEntry) return;
        const { spIdx, piecePrice, costPerBdFt } = editingSupplierEntry;
        const updatedPricing = [...(item.supplierPricing || [])];
        const prev = updatedPricing[spIdx];
        if (!prev) return;
        updatedPricing[spIdx] = mergeSupplierPriceEntry(
            prev,
            {
                supplier: prev.supplier,
                piecePrice,
                costPerBdFt,
                notes: prev.notes,
            },
            'manual',
        );
        const updates: any = { supplierPricing: updatedPricing };
        // If editing the active supplier, update top-level too
        if (prev.supplier === item.supplier) {
            updates.piecePrice = piecePrice;
            updates.costPerBdFt = costPerBdFt;
        }
        await persistWoodItem(item.id, updates);
        setEditingSupplierEntry(null);
    };

    // Set preferred supplier from dropdown (syncs piecePrice and costPerBdFt from that supplier's entry)
    const handleSetPreferredSupplier = useCallback(async (item: WoodPricingItem, supplierName: string) => {
        const sp = item.supplierPricing?.find(s => s.supplier === supplierName);
        if (!sp) return;
        await persistWoodItem(item.id, {
            supplier: supplierName,
            piecePrice: sp.piecePrice ?? 0,
            costPerBdFt: sp.costPerBdFt ?? 0,
        });
        setSupplierDropdownOpen(null);
    }, [persistWoodItem]);

    // Close supplier dropdown on click outside
    useEffect(() => {
        const handleClickOutside = (e: MouseEvent) => {
            if (supplierDropdownRef.current && !supplierDropdownRef.current.contains(e.target as Node)) {
                setSupplierDropdownOpen(null);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    // CSV Import Logic
    const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;
        const reader = new FileReader();
        reader.onload = (event) => {
            const text = event.target?.result as string;
            setCsvText(text);
            parseCSV(text);
        };
        reader.readAsText(file);
    };

    const parseCSV = (text: string) => {
        setImportError('');
        const lines = text.trim().split('\n');
        if (lines.length === 0) {
            setImportError('CSV is empty');
            return;
        }

        const items: TimberItemInput[] = [];
        const startIndex = lines[0].toLowerCase().includes('size') || lines[0].toLowerCase().includes('price') ? 1 : 0;

        for (let i = startIndex; i < lines.length; i++) {
            const line = lines[i].trim();
            if (!line) continue;

            const values = line.includes('\t') ? line.split('\t') : line.split(',');
            if (values.length < 2) {
                setImportError(`Line ${i + 1}: Expected at least 2 columns (Size, Price)`);
                continue;
            }

            const dimensions = values[0].trim();
            const piecePrice = parseFloat(values[1].replace(/[$,]/g, '').trim()) || 0;
            let costPerBdFt = values[2] ? parseFloat(values[2].replace(/[$,]/g, '').trim()) : 0;

            const boardFeet = parseDimensions(dimensions);

            if (!costPerBdFt && boardFeet > 0 && piecePrice > 0) {
                costPerBdFt = piecePrice / boardFeet;
            }

            if (boardFeet === 0) {
                setImportError(`Line ${i + 1}: Could not parse dimensions "${dimensions}"`);
                continue;
            }

            items.push({
                species: importSpecies || 'Unknown Species',
                grade: importGrade,
                dimensions,
                moistureContent: importMoisture || undefined,
                piecePrice,
                costPerBdFt: Math.round(costPerBdFt * 100) / 100,
                boardFeet: Math.round(boardFeet * 100) / 100,
                supplier: importSupplier || undefined,
            });
        }

        setImportPreview(items);
    };

    const handleImportConfirm = async () => {
        if (importPreview.length === 0) return;
        setImporting(true);
        try {
            if (activeTab === 'glulam') {
                const glulamRows: GlulamItemInput[] = importPreview.map((row) => ({
                    species: row.species,
                    grade: row.grade,
                    dimensions: row.dimensions,
                    piecePrice: row.piecePrice,
                    costPerBdFt: row.costPerBdFt,
                    boardFeet: row.boardFeet,
                    supplier: row.supplier,
                }));
                await importGlulamItems(glulamRows);
            } else {
                await importTimberItems(importPreview);
            }
            setShowImportModal(false);
            setCsvText('');
            setImportPreview([]);
            setImportSpecies('');
            setImportGrade('#1');
            setImportMoisture('');
            setImportSupplier('');
            setImportError('');
        } catch (error) {
            console.error('Import error:', error);
            setImportError('Failed to import items');
        } finally {
            setImporting(false);
        }
    };

    const handleImportCancel = () => {
        setShowImportModal(false);
        setCsvText('');
        setImportPreview([]);
        setImportSpecies('');
        setImportGrade('#1');
        setImportMoisture('');
        setImportSupplier('');
        setImportError('');
    };

    // Bulk edit logic
    const toggleItemSelection = (id: string) => {
        const next = new Set(selectedIds);
        if (next.has(id)) {
            next.delete(id);
        } else {
            next.add(id);
        }
        setSelectedIds(next);
    };

    const selectAllInSpecies = (species: string) => {
        const speciesItems = activeGroupedWood[species] || [];
        const next = new Set(selectedIds);
        speciesItems.forEach(item => next.add(item.id));
        setSelectedIds(next);
    };

    const deselectAllInSpecies = (species: string) => {
        const speciesItems = activeGroupedWood[species] || [];
        const next = new Set(selectedIds);
        speciesItems.forEach(item => next.delete(item.id));
        setSelectedIds(next);
    };

    const handleBulkEditConfirm = async () => {
        if (selectedIds.size === 0) return;
        setBulkUpdating(true);
        try {
            const updates: Partial<TimberItemInput> = {};
            if (bulkEditSpecies) updates.species = bulkEditSpecies;
            if (bulkEditGrade) updates.grade = bulkEditGrade;
            if (bulkEditSupplier) updates.supplier = bulkEditSupplier;
            if (bulkEditCostPerBdFt) {
                const newCostPerBdFt = parseFloat(bulkEditCostPerBdFt);
                if (!isNaN(newCostPerBdFt)) {
                    updates.costPerBdFt = newCostPerBdFt;
                }
            }
            if (bulkEditMoisture) {
                updates.moistureContent =
                    bulkEditMoisture === 'unspecified' ? '' : (bulkEditMoisture as 'green' | 'kiln_dried');
            }

            if (Object.keys(updates).length > 0) {
                if (activeTab === 'glulam') {
                    const glulamUpdates: Partial<GlulamItemInput> = { ...updates };
                    delete (glulamUpdates as Partial<TimberItemInput>).moistureContent;
                    await bulkUpdateGlulamItems(Array.from(selectedIds), glulamUpdates);
                } else {
                    await bulkUpdateTimberItems(Array.from(selectedIds), updates);
                }
            }

            setShowBulkEditModal(false);
            setBulkEditSpecies('');
            setBulkEditGrade('');
            setBulkEditCostPerBdFt('');
            setBulkEditSupplier('');
            setBulkEditMoisture('');
            setSelectedIds(new Set());
            setBulkEditMode(false);
        } catch (error) {
            console.error('Bulk edit error:', error);
        } finally {
            setBulkUpdating(false);
        }
    };

    const handleBulkDelete = async () => {
        if (selectedIds.size === 0) return;
        if (!window.confirm(`Delete ${selectedIds.size} selected items?`)) return;

        try {
            if (activeTab === 'glulam') {
                await bulkDeleteGlulamItems(Array.from(selectedIds));
            } else {
                await bulkDeleteTimberItems(Array.from(selectedIds));
            }
            setSelectedIds(new Set());
            setBulkEditMode(false);
        } catch (error) {
            console.error('Bulk delete error:', error);
        }
    };

    return (
        <div className="materials-page">
            <AppHeader />

            <main className="materials-main">
                <div className="materials-header">
                    <div className="materials-title-group">
                        <h1>
                            <Database size={32} />
                            Materials Database
                        </h1>
                        <p className="materials-subtitle">Manage timber, glulam, hardware costs, and supplier info.</p>
                        <p className="materials-star-note">★ = default supplier used on proposals</p>
                    </div>

                    <div className="materials-actions">
                        {bulkEditMode ? (
                            <>
                                <span className="bulk-selection-count">{selectedIds.size} selected</span>
                                <button
                                    className="materials-action-btn secondary"
                                    onClick={() => setShowBulkEditModal(true)}
                                    disabled={selectedIds.size === 0}
                                >
                                    <Edit2 size={16} /> Bulk Edit
                                </button>
                                <button
                                    className="materials-action-btn danger-btn"
                                    onClick={handleBulkDelete}
                                    disabled={selectedIds.size === 0}
                                >
                                    <Trash2 size={16} /> Delete
                                </button>
                                <button
                                    className="materials-action-btn secondary"
                                    onClick={() => {
                                        setBulkEditMode(false);
                                        setSelectedIds(new Set());
                                    }}
                                >
                                    Cancel
                                </button>
                            </>
                        ) : (
                            <>
                                <button className="materials-action-btn secondary" onClick={() => setBulkEditMode(true)}>
                                    <CheckSquare size={16} /> Bulk Edit
                                </button>
                                <div className="add-item-dropdown-wrapper">
                                    <button
                                        className="materials-action-btn primary"
                                        onClick={() => setShowAddDropdown(prev => !prev)}
                                    >
                                        <Plus size={16} /> Add Item <ChevronDown size={14} />
                                    </button>
                                    {showAddDropdown && (
                                        <div className="add-item-dropdown">
                                            <button onClick={() => { setShowAddDropdown(false); handleAdd(); }}>
                                                <Plus size={16} /> New Item
                                            </button>
                                            <button onClick={() => { setShowAddDropdown(false); setShowSupplierImportModal(true); }}>
                                                <FileText size={16} /> Supplier Import
                                            </button>
                                            <button onClick={() => { setShowAddDropdown(false); setShowSnapshotsModal(true); }}>
                                                <History size={16} /> Catalog Snapshots
                                            </button>
                                            <div className="dropdown-divider" />
                                            <button onClick={() => { setShowAddDropdown(false); setShowTimberReport(true); }}>
                                                <BarChart3 size={16} /> Timber Report
                                            </button>
                                        </div>
                                    )}
                                </div>
                            </>
                        )}
                    </div>
                </div>

                {/* Tabs */}
                <div className="materials-tabs">
                    <button
                        className={`materials-tab ${activeTab === 'timber' ? 'active' : ''}`}
                        onClick={() => setActiveTab('timber')}
                    >
                        Timbers
                    </button>
                    <button
                        className={`materials-tab ${activeTab === 'glulam' ? 'active' : ''}`}
                        onClick={() => setActiveTab('glulam')}
                    >
                        Glulams
                    </button>
                    <button
                        className={`materials-tab ${activeTab === 'hardware' ? 'active' : ''}`}
                        onClick={() => setActiveTab('hardware')}
                    >
                        Hardware & Fasteners
                    </button>
                </div>

                {/* Timber/glulam quotes auto-apply; only hardware still needs staff Approve. */}
                {activeTab === 'hardware' && (
                  <MaterialsPriceUpdateReviewPanel catalogDomain="hardware" />
                )}

                {/* Search Bar */}
                {isWoodTab && (
                    <div className="materials-search">
                        <Search size={18} className="search-icon" />
                        <input
                            type="text"
                            placeholder={activeTab === 'glulam'
                                ? 'Search by species, grade, or dimensions...'
                                : 'Search by species, grade, dryness (green/kiln), or dimensions...'}
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            className="materials-search-input"
                        />
                        {searchQuery && (
                            <button
                                className="search-clear-btn"
                                onClick={() => setSearchQuery('')}
                            >
                                <X size={16} />
                            </button>
                        )}
                    </div>
                )}

                {/* Content */}
                <div className="materials-content">
                    {isWoodTab ? (
                        woodLoading ? (
                            <div className="empty-state">{activeTab === 'glulam' ? 'Loading glulam items...' : 'Loading timber items...'}</div>
                        ) : (
                            <div className="species-groups">
                                {Object.entries(activeGroupedWood).map(([species, items]) => {
                                    const allSelected = items.every(item => selectedIds.has(item.id));
                                    const someSelected = items.some(item => selectedIds.has(item.id));

                                    return (
                                        <div key={species} className={`species-group ${items.some(i => i.id === supplierDropdownOpen) ? 'supplier-dropdown-open' : ''}`}>
                                            <button
                                                className="species-group-header"
                                                onClick={() => toggleSpecies(species)}
                                            >
                                                {expandedSpecies.has(species) ? <ChevronDown size={20} /> : <ChevronRight size={20} />}
                                                <span className="species-name">{species}</span>
                                                <span className="species-count">{items.length} items</span>
                                                {bulkEditMode && (
                                                    <span
                                                        className="species-select-all"
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            allSelected ? deselectAllInSpecies(species) : selectAllInSpecies(species);
                                                        }}
                                                    >
                                                        {allSelected ? 'Deselect All' : 'Select All'}
                                                    </span>
                                                )}
                                            </button>

                                            {expandedSpecies.has(species) && (
                                                <div className={`materials-table-container ${items.some(i => i.id === supplierDropdownOpen) ? 'supplier-dropdown-open' : ''}`}>
                                                    <table className="materials-table">
                                                        <thead>
                                                            <tr>
                                                                {bulkEditMode && <th className="col-checkbox"></th>}
                                                                <th className="sortable-header" onClick={() => handleSort('grade')}>
                                                                    Grade {getSortIcon('grade')}
                                                                </th>
                                                                {activeTab === 'timber' && (
                                                                    <th className="sortable-header" onClick={() => handleSort('moisture')}>
                                                                        Dryness {getSortIcon('moisture')}
                                                                    </th>
                                                                )}
                                                                <th className="sortable-header" onClick={() => handleSort('dimensions')}>
                                                                    <span className="sort-header-content">
                                                                        Dimensions {getSortIcon('dimensions')}
                                                                        {sortConfig.field === 'dimensions' && (
                                                                            <button
                                                                                className="dimension-order-btn"
                                                                                onClick={(e) => { e.stopPropagation(); cycleDimensionOrder(); }}
                                                                                title="Change sort order (H×W×L)"
                                                                            >
                                                                                {sortConfig.dimensionOrder[0][0].toUpperCase()}
                                                                            </button>
                                                                        )}
                                                                    </span>
                                                                </th>
                                                                <th className="sortable-header" onClick={() => handleSort('boardFeet')}>
                                                                    Board Feet {getSortIcon('boardFeet')}
                                                                </th>
                                                                <th className="sortable-header" onClick={() => handleSort('piecePrice')}>
                                                                    Piece Price {getSortIcon('piecePrice')}
                                                                </th>
                                                                <th className="sortable-header" onClick={() => handleSort('costPerBdFt')}>
                                                                    $/bdft {getSortIcon('costPerBdFt')}
                                                                </th>
                                                                <th>Supplier</th>
                                                                <th className="col-trend">Trend</th>
                                                                {!bulkEditMode && <th className="col-actions">Actions</th>}
                                                            </tr>
                                                        </thead>
                                                        <tbody>
                                                            {items.map((item) => (
                                                                <React.Fragment key={item.id}>
                                                                    <tr className={`${selectedIds.has(item.id) ? 'selected-row' : ''} ${supplierDropdownOpen === item.id ? 'supplier-dropdown-row-active' : ''}`}>
                                                                        {bulkEditMode && (
                                                                            <td className="col-checkbox">
                                                                                <button
                                                                                    className="checkbox-btn"
                                                                                    onClick={() => toggleItemSelection(item.id)}
                                                                                >
                                                                                    {selectedIds.has(item.id) ? <CheckSquare size={18} /> : <Square size={18} />}
                                                                                </button>
                                                                            </td>
                                                                        )}
                                                                        <td><span className="badge green">{item.grade}</span></td>
                                                                        {activeTab === 'timber' && (
                                                                            <td><span className="badge gray">{formatMoistureLabel((item as TimberItem).moistureContent)}</span></td>
                                                                        )}
                                                                        <td>
                                                                            <strong>{item.dimensions}</strong>
                                                                            {hasOddDimension(item.dimensions) && (
                                                                                <span className="odd-dim-flag" data-tooltip="Non-standard"><Flag size={13} /></span>
                                                                            )}
                                                                        </td>
                                                                        <td>{item.boardFeet.toFixed(2)}</td>
                                                                        <td>{formatCurrency(item.piecePrice)}</td>
                                                                        <td>{formatCurrency(item.costPerBdFt)}</td>
                                                                        <td>
                                                                            {item.supplierPricing && item.supplierPricing.length > 0 ? (
                                                                                <div
                                                                                    className="supplier-dropdown-wrap"
                                                                                    ref={supplierDropdownOpen === item.id ? supplierDropdownRef : undefined}
                                                                                >
                                                                                    <div className="supplier-cell-inner">
                                                                                        <button
                                                                                            className="supplier-dropdown-trigger"
                                                                                            onClick={() => setSupplierDropdownOpen(prev => prev === item.id ? null : item.id)}
                                                                                            title="Select preferred supplier"
                                                                                        >
                                                                                            <span className={`badge ${item.supplier ? 'blue' : 'gray'}`}>{item.supplier || '—'}</span>
                                                                                            <span className="supplier-count">{item.supplierPricing.length}</span>
                                                                                            <ChevronDown size={14} className={supplierDropdownOpen === item.id ? 'rotated' : ''} />
                                                                                        </button>
                                                                                        <button
                                                                                            className="supplier-expand-btn"
                                                                                            onClick={() => {
                                                                                                setExpandedSuppliers(prev => {
                                                                                                    const next = new Set(prev);
                                                                                                    next.has(item.id) ? next.delete(item.id) : next.add(item.id);
                                                                                                    return next;
                                                                                                });
                                                                                            }}
                                                                                            title={expandedSuppliers.has(item.id) ? 'Collapse details' : 'Expand supplier details'}
                                                                                        >
                                                                                            {expandedSuppliers.has(item.id) ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                                                                                        </button>
                                                                                    </div>
                                                                                    {supplierDropdownOpen === item.id && (
                                                                                        <div className="supplier-dropdown-menu">
                                                                                            {item.supplierPricing.map((sp: SupplierPrice) => (
                                                                                                <button
                                                                                                    key={sp.supplier}
                                                                                                    className={`supplier-dropdown-option ${sp.supplier === item.supplier ? 'selected' : ''}`}
                                                                                                    onClick={() => handleSetPreferredSupplier(item, sp.supplier)}
                                                                                                >
                                                                                                    <span>{sp.supplier}</span>
                                                                                                    <span className="supplier-dropdown-price">{formatCurrency(sp.piecePrice)}</span>
                                                                                                    {sp.supplier === item.supplier && <span className="active-indicator">★</span>}
                                                                                                </button>
                                                                                            ))}
                                                                                        </div>
                                                                                    )}
                                                                                </div>
                                                                            ) : (
                                                                                <span className={`badge ${item.supplier ? 'blue' : 'gray'}`}>{item.supplier || '—'}</span>
                                                                            )}
                                                                        </td>
                                                                        <td className="col-trend">
                                                                            <TimberSupplierPriceTrend
                                                                                supplierEntry={
                                                                                    item.supplierPricing?.find((s) => s.supplier === item.supplier)
                                                                                    || item.supplierPricing?.[0]
                                                                                    || null
                                                                                }
                                                                                supplierLabel={item.supplier}
                                                                                skuLabel={`${item.grade} · ${item.dimensions}`}
                                                                            />
                                                                        </td>
                                                                        {!bulkEditMode && (
                                                                            <td className="col-actions">
                                                                                <button className="action-icon-btn" onClick={() => handleEdit(item.id)} title="Edit">
                                                                                    <Edit2 size={16} />
                                                                                </button>
                                                                                <button className="action-icon-btn danger" onClick={() => handleDelete(item.id)} title="Delete">
                                                                                    <Trash2 size={16} />
                                                                                </button>
                                                                            </td>
                                                                        )}
                                                                    </tr>
                                                                    {expandedSuppliers.has(item.id) && item.supplierPricing && item.supplierPricing.map((sp: SupplierPrice, spIdx: number) => {
                                                                        const isEditing = editingSupplierEntry?.itemId === item.id && editingSupplierEntry?.spIdx === spIdx;
                                                                        return (
                                                                            <tr key={`${item.id}-sp-${spIdx}`} className="supplier-sub-row">
                                                                                {bulkEditMode && <td></td>}
                                                                                <td></td>
                                                                                {activeTab === 'timber' && <td></td>}
                                                                                <td></td>
                                                                                <td></td>
                                                                                <td></td>
                                                                                <td className={sp.supplier === item.supplier ? 'active-supplier-price' : ''}>
                                                                                    {isEditing ? (
                                                                                        <input
                                                                                            type="number"
                                                                                            className="sp-inline-input"
                                                                                            value={editingSupplierEntry!.piecePrice}
                                                                                            onChange={(e) => setEditingSupplierEntry(prev => prev ? { ...prev, piecePrice: parseFloat(e.target.value) || 0 } : null)}
                                                                                            step="0.01"
                                                                                        />
                                                                                    ) : formatCurrency(sp.piecePrice)}
                                                                                </td>
                                                                                <td className={sp.supplier === item.supplier ? 'active-supplier-price' : ''}>
                                                                                    {isEditing ? (
                                                                                        <input
                                                                                            type="number"
                                                                                            className="sp-inline-input"
                                                                                            value={editingSupplierEntry!.costPerBdFt}
                                                                                            onChange={(e) => setEditingSupplierEntry(prev => prev ? { ...prev, costPerBdFt: parseFloat(e.target.value) || 0 } : null)}
                                                                                            step="0.01"
                                                                                        />
                                                                                    ) : formatCurrency(sp.costPerBdFt)}
                                                                                </td>
                                                                                <td>
                                                                                    <span className="badge blue">{sp.supplier}</span>
                                                                                    {sp.supplier === item.supplier && (
                                                                                        <span className="active-indicator" title="Active price">★</span>
                                                                                    )}
                                                                                </td>
                                                                                <td className="col-trend">
                                                                                    <TimberSupplierPriceTrend
                                                                                        supplierEntry={sp}
                                                                                        supplierLabel={sp.supplier}
                                                                                        skuLabel={`${item.grade} · ${item.dimensions}`}
                                                                                    />
                                                                                </td>
                                                                                {!bulkEditMode && (
                                                                                    <td className="col-actions">
                                                                                        {isEditing ? (
                                                                                            <>
                                                                                                <button className="action-icon-btn" onClick={() => handleSaveSupplierEntry(item)} title="Save">
                                                                                                    <Edit2 size={14} />
                                                                                                </button>
                                                                                                <button className="action-icon-btn" onClick={() => setEditingSupplierEntry(null)} title="Cancel">
                                                                                                    <X size={14} />
                                                                                                </button>
                                                                                            </>
                                                                                        ) : (
                                                                                            <>
                                                                                                <button className="action-icon-btn" onClick={() => setEditingSupplierEntry({ itemId: item.id, spIdx, piecePrice: sp.piecePrice, costPerBdFt: sp.costPerBdFt })} title="Edit price">
                                                                                                    <Edit2 size={14} />
                                                                                                </button>
                                                                                                <button className="action-icon-btn danger" onClick={() => handleDeleteSupplierEntry(item, spIdx)} title="Remove supplier">
                                                                                                    <Trash2 size={14} />
                                                                                                </button>
                                                                                            </>
                                                                                        )}
                                                                                    </td>
                                                                                )}
                                                                            </tr>
                                                                        );
                                                                    })}
                                                                </React.Fragment>
                                                            ))}
                                                        </tbody>
                                                    </table>
                                                </div>
                                            )}
                                        </div>
                                    );
                                })}
                                {Object.keys(activeGroupedWood).length === 0 && (
                                    <div className="empty-state">
                                        <p>{activeTab === 'glulam'
                                            ? 'No glulam items yet. Import a CSV or add items manually.'
                                            : 'No timber items yet. Import a CSV or add items manually.'}</p>
                                    </div>
                                )}
                            </div>
                        )
                    ) : (
                        <div className="hardware-content">
                            {/* Category filter tabs */}
                            <div className="hardware-categories">
                                <button
                                    className={`category-btn ${selectedCategory === 'all' ? 'active' : ''}`}
                                    onClick={() => setSelectedCategory('all')}
                                >
                                    All
                                </button>
                                {HARDWARE_CATEGORIES.map(cat => (
                                    <button
                                        key={cat}
                                        className={`category-btn ${selectedCategory === cat ? 'active' : ''}`}
                                        onClick={() => setSelectedCategory(cat)}
                                    >
                                        {cat}
                                    </button>
                                ))}
                            </div>
                            <div className="species-groups">
                                {Object.entries(groupedHardware).map(([supplier, items]) => (
                                    <div key={supplier} className="species-group">
                                        <button
                                            type="button"
                                            className="species-group-header"
                                            onClick={() => toggleHardwareSupplier(supplier)}
                                        >
                                            {expandedHardwareSuppliers.has(supplier)
                                                ? <ChevronDown size={20} />
                                                : <ChevronRight size={20} />}
                                            <span className="species-name">{supplier}</span>
                                            <span className="species-count">{items.length} items</span>
                                        </button>
                                        {expandedHardwareSuppliers.has(supplier) && (
                                            <div className="materials-table-container">
                                                <table className="materials-table">
                                                    <thead>
                                                        <tr>
                                                            <th>Description</th>
                                                            <th title="Diameter × length">Ø × L</th>
                                                            <th>SKU</th>
                                                            <th>Price</th>
                                                            <th className="col-trend">Trend</th>
                                                            <th>Unit</th>
                                                            <th>Category</th>
                                                            <th className="col-actions">Actions</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {items.map((item) => {
                                                            const dims = resolveHardwareDiameterLengthForDisplay(
                                                                item.category,
                                                                item.name,
                                                                item.description,
                                                                item.sku,
                                                            );
                                                            const primarySp =
                                                                (item.supplierPricing || []).find(
                                                                    (sp) =>
                                                                        (sp.supplier || '').trim().toLowerCase()
                                                                        === (item.supplier || '').trim().toLowerCase(),
                                                                )
                                                                || (item.supplierPricing || [])[0]
                                                                || null;
                                                            const dimLabel = formatHardwareDiameterLengthLabel(dims);
                                                            return (
                                                                <tr key={item.id}>
                                                                    <td>
                                                                        <strong>{item.name || '—'}</strong>
                                                                        {item.description
                                                                            ? (
                                                                                <div className="hardware-desc-sub">
                                                                                    {item.description}
                                                                                </div>
                                                                            )
                                                                            : null}
                                                                    </td>
                                                                    <td>
                                                                        <strong>{dimLabel}</strong>
                                                                    </td>
                                                                    <td><code>{item.sku || '—'}</code></td>
                                                                    <td>{formatHardwarePrice(item)}</td>
                                                                    <td className="col-trend">
                                                                        <HardwareSupplierPriceTrend
                                                                            supplierEntry={primarySp}
                                                                            supplierLabel={primarySp?.supplier || item.supplier}
                                                                            skuLabel={[
                                                                                dims?.labelWithMark,
                                                                                item.sku,
                                                                                item.name,
                                                                            ].filter(Boolean).join(' · ')}
                                                                        />
                                                                    </td>
                                                                    <td>{item.unit || 'each'}</td>
                                                                    <td>
                                                                        <span className={`category-badge ${item.category.toLowerCase().replace(' ', '-')}`}>
                                                                            {item.category}
                                                                        </span>
                                                                    </td>
                                                                    <td className="col-actions">
                                                                        <button className="action-icon-btn" onClick={() => handleEdit(item.id)} title="Edit">
                                                                            <Edit2 size={16} />
                                                                        </button>
                                                                        <button className="action-icon-btn danger" onClick={() => deleteHardwareItem(item.id)} title="Delete">
                                                                            <Trash2 size={16} />
                                                                        </button>
                                                                    </td>
                                                                </tr>
                                                            );
                                                        })}
                                                    </tbody>
                                                </table>
                                            </div>
                                        )}
                                    </div>
                                ))}
                                {Object.keys(groupedHardware).length === 0 && (
                                    <div className="empty-state">
                                        <p>No hardware items in this category. Add items to get started.</p>
                                    </div>
                                )}
                            </div>
                        </div>
                    )}
                </div>
            </main>

            {/* CSV Import Modal */}
            {showImportModal && (
                <div className="import-modal-overlay" onClick={handleImportCancel}>
                    <div className="import-modal" onClick={(e) => e.stopPropagation()}>
                        <div className="import-modal-header">
                            <h2><FileText size={24} /> Import Timber CSV</h2>
                            <button className="import-modal-close" onClick={handleImportCancel}>
                                <X size={20} />
                            </button>
                        </div>

                        <div className="import-modal-body">
                            <div className="import-field">
                                <label>Species Name</label>
                                <input
                                    type="text"
                                    value={importSpecies}
                                    onChange={(e) => setImportSpecies(e.target.value)}
                                    placeholder="e.g., White Oak, Douglas Fir"
                                />
                                <span className="field-hint">All imported items will be assigned to this species</span>
                            </div>

                                    <div className="import-field">
                                        <label>Grade</label>
                                        <select
                                            value={importGrade}
                                            onChange={(e) => setImportGrade(e.target.value)}
                                            className="import-select"
                                        >
                                            {GRADE_OPTIONS.map(grade => (
                                                <option key={grade} value={grade}>{grade}</option>
                                            ))}
                                        </select>
                                        <span className="field-hint">Lumber grade for all imported items</span>
                                    </div>

                                    <div className="import-field">
                                        <label>Dryness</label>
                                        <select
                                            value={importMoisture}
                                            onChange={(e) => setImportMoisture(e.target.value as TimberMoistureContent)}
                                            className="import-select"
                                        >
                                            <option value="">Unspecified</option>
                                            <option value="green">Green</option>
                                            <option value="kiln_dried">Kiln dried</option>
                                        </select>
                                        <span className="field-hint">Applied to every row in this import</span>
                                    </div>

                                    <div className="import-field">
                                <label>Supplier (optional)</label>
                                <SupplierCombobox
                                    value={importSupplier}
                                    onChange={setImportSupplier}
                                    suggestions={supplierNames}
                                    placeholder="Select or type a supplier"
                                    hint="Tag imported items with this supplier name"
                                />
                            </div>

                            <div className="import-field">
                                <label>CSV File or Paste Data</label>
                                <div className="import-file-row">
                                    <input
                                        type="file"
                                        accept=".csv,.txt"
                                        ref={fileInputRef}
                                        onChange={handleFileSelect}
                                        style={{ display: 'none' }}
                                    />
                                    <button
                                        className="materials-action-btn secondary"
                                        onClick={() => fileInputRef.current?.click()}
                                    >
                                        <Upload size={16} /> Choose File
                                    </button>
                                </div>
                                <textarea
                                    value={csvText}
                                    onChange={(e) => {
                                        setCsvText(e.target.value);
                                        parseCSV(e.target.value);
                                    }}
                                    placeholder="Or paste CSV data here:
Size,Piece Price,$/bdft
4x8x12,240,5.00
6x6x10,180,"
                                    rows={6}
                                />
                                <span className="field-hint">Format: Timber Size (e.g., 4x8x12), Piece Price, $/bdft (optional)</span>
                            </div>

                            {importError && (
                                <div className="import-error">
                                    <AlertCircle size={16} /> {importError}
                                </div>
                            )}

                            {importPreview.length > 0 && (
                                <div className="import-preview">
                                    <h4>Preview ({importPreview.length} items)</h4>
                                    <div className="import-preview-table-wrap">
                                        <table className="materials-table import-preview-table">
                                            <thead>
                                                <tr>
                                                    <th>Dimensions</th>
                                                    <th>Board Feet</th>
                                                    <th>Piece Price</th>
                                                    <th>$/bdft</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {importPreview.slice(0, 5).map((item, idx) => (
                                                    <tr key={idx}>
                                                        <td>{item.dimensions}</td>
                                                        <td>{item.boardFeet.toFixed(2)}</td>
                                                        <td>{formatCurrency(item.piecePrice)}</td>
                                                        <td>{formatCurrency(item.costPerBdFt)}</td>
                                                    </tr>
                                                ))}
                                                {importPreview.length > 5 && (
                                                    <tr>
                                                        <td colSpan={4} className="preview-more">...and {importPreview.length - 5} more</td>
                                                    </tr>
                                                )}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            )}
                        </div>

                        <div className="import-modal-footer">
                            <button className="materials-action-btn secondary" onClick={handleImportCancel}>
                                Cancel
                            </button>
                            <button
                                className="materials-action-btn primary"
                                onClick={handleImportConfirm}
                                disabled={importPreview.length === 0 || !importSpecies || importing}
                            >
                                {importing ? 'Importing...' : `Import ${importPreview.length} Items`}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Add/Edit Item Modal */}
            {showItemModal && (
                <div className="import-modal-overlay" onClick={() => setShowItemModal(false)}>
                    <div className="import-modal" onClick={(e) => e.stopPropagation()}>
                        <div className="import-modal-header">
                            <h2><Edit2 size={24} /> {
                                activeTab === 'glulam'
                                    ? (editingGlulamItem ? 'Edit Glulam Item' : 'Add Glulam Item')
                                    : activeTab === 'timber'
                                        ? (editingItem ? 'Edit Timber Item' : 'Add Timber Item')
                                        : (editingHardwareItem ? 'Edit Hardware Item' : 'Add Hardware Item')
                            }</h2>
                            <button className="import-modal-close" onClick={() => setShowItemModal(false)}>
                                <X size={20} />
                            </button>
                        </div>

                        <div className="import-modal-body">
                            {isWoodTab ? (
                                <>
                                    <div className="import-field">
                                        <label>Species Name *</label>
                                        <input
                                            type="text"
                                            value={itemForm.species}
                                            onChange={(e) => setItemForm({ ...itemForm, species: e.target.value })}
                                            placeholder={activeTab === 'glulam' ? 'e.g., Douglas Fir, SPF' : 'e.g., White Oak, Douglas Fir'}
                                        />
                                    </div>

                                    <div className="import-field">
                                        <label>Grade *</label>
                                        <select
                                            value={itemForm.grade}
                                            onChange={(e) => setItemForm({ ...itemForm, grade: e.target.value })}
                                            className="import-select"
                                        >
                                            {(activeTab === 'glulam' ? GLULAM_GRADE_OPTIONS : GRADE_OPTIONS).map((grade) => (
                                                <option key={grade} value={grade}>{grade}</option>
                                            ))}
                                        </select>
                                    </div>

                                    {activeTab === 'timber' && (
                                        <div className="import-field">
                                            <label>Dryness</label>
                                            <select
                                                value={itemForm.moistureContent}
                                                onChange={(e) => setItemForm({ ...itemForm, moistureContent: e.target.value as TimberMoistureContent })}
                                                className="import-select"
                                            >
                                                <option value="">Unspecified</option>
                                                <option value="green">Green</option>
                                                <option value="kiln_dried">Kiln dried</option>
                                            </select>
                                            <span className="field-hint">Green vs kiln dried — separate prices per size in the catalog</span>
                                        </div>
                                    )}

                                    <div className="import-field">
                                        <label>Dimensions (HxWxL) *</label>
                                        <input
                                            type="text"
                                            value={itemForm.dimensions}
                                            onChange={(e) => {
                                                const dims = e.target.value;
                                                const bf = parseDimensions(dims);
                                                const updates: any = { dimensions: dims };
                                                if (bf > 0) {
                                                    updates.boardFeet = Math.round(bf * 100) / 100;
                                                    if (itemForm.costPerBdFt > 0) {
                                                        updates.piecePrice = Math.round(bf * itemForm.costPerBdFt * 100) / 100;
                                                    } else if (itemForm.piecePrice > 0) {
                                                        updates.costPerBdFt = Math.round(itemForm.piecePrice / bf * 100) / 100;
                                                    }
                                                }
                                                setItemForm({ ...itemForm, ...updates });
                                            }}
                                            placeholder="e.g., 6x6x8"
                                        />
                                        <span className="field-hint">Board feet auto-calculated from dimensions</span>
                                    </div>

                                    <div className="import-field">
                                        <label>Board Feet</label>
                                        <input
                                            type="number"
                                            step="0.01"
                                            value={itemForm.boardFeet}
                                            onChange={(e) => {
                                                const bf = parseFloat(e.target.value) || 0;
                                                const updates: any = { boardFeet: bf };
                                                if (bf > 0 && itemForm.costPerBdFt > 0) {
                                                    updates.piecePrice = Math.round(bf * itemForm.costPerBdFt * 100) / 100;
                                                }
                                                setItemForm({ ...itemForm, ...updates });
                                            }}
                                            placeholder="Auto-calculated from dimensions"
                                            readOnly={parseDimensions(itemForm.dimensions) > 0}
                                        />
                                        {parseDimensions(itemForm.dimensions) > 0 && (
                                            <span className="field-hint">Locked — calculated from dimensions</span>
                                        )}
                                    </div>

                                    <div className="import-field">
                                        <label>Cost per Board Foot ($)</label>
                                        <input
                                            type="number"
                                            step="0.01"
                                            value={itemForm.costPerBdFt}
                                            onChange={(e) => {
                                                const cpbf = parseFloat(e.target.value) || 0;
                                                const updates: any = { costPerBdFt: cpbf };
                                                if (cpbf > 0 && itemForm.boardFeet > 0) {
                                                    updates.piecePrice = Math.round(itemForm.boardFeet * cpbf * 100) / 100;
                                                }
                                                setItemForm({ ...itemForm, ...updates });
                                            }}
                                            placeholder="e.g., 5.25"
                                        />
                                        <span className="field-hint">Piece price auto-calculated: board feet × $/bf</span>
                                    </div>

                                    <div className="import-field">
                                        <label>Piece Price ($)</label>
                                        <input
                                            type="number"
                                            step="0.01"
                                            value={itemForm.piecePrice}
                                            onChange={(e) => {
                                                const pp = parseFloat(e.target.value) || 0;
                                                const updates: any = { piecePrice: pp };
                                                if (pp > 0 && itemForm.boardFeet > 0) {
                                                    updates.costPerBdFt = Math.round(pp / itemForm.boardFeet * 100) / 100;
                                                }
                                                setItemForm({ ...itemForm, ...updates });
                                            }}
                                            placeholder="e.g., 245.00"
                                        />
                                        <span className="field-hint">Or enter piece price directly — $/bf auto-calculated</span>
                                    </div>

                                    <div className="import-field">
                                        <label>Supplier</label>
                                        <SupplierCombobox
                                            value={itemForm.supplier}
                                            onChange={(val) => setItemForm({ ...itemForm, supplier: val })}
                                            suggestions={supplierNames}
                                            placeholder="Select or type a supplier"
                                            hint="Optional — tag which supplier this price is from"
                                        />
                                    </div>
                                </>
                            ) : (
                                <>
                                    <div className="import-field">
                                        <label>Category *</label>
                                        <select
                                            value={hardwareForm.category}
                                            onChange={(e) => setHardwareForm({ ...hardwareForm, category: e.target.value as HardwareCategory })}
                                            className="import-select"
                                        >
                                            {HARDWARE_CATEGORIES.map(cat => (
                                                <option key={cat} value={cat}>{cat}</option>
                                            ))}
                                        </select>
                                    </div>

                                    <div className="import-field">
                                        <label>Item Name *</label>
                                        <input
                                            type="text"
                                            value={hardwareForm.name}
                                            onChange={(e) => setHardwareForm({ ...hardwareForm, name: e.target.value })}
                                            placeholder="e.g., Timber Lok Screws, Oak Pegs"
                                        />
                                    </div>

                                    <div className="import-field">
                                        <label>SKU</label>
                                        <input
                                            type="text"
                                            value={hardwareForm.sku}
                                            onChange={(e) => setHardwareForm({ ...hardwareForm, sku: e.target.value })}
                                            placeholder="e.g., VBH440 (Rothoblaas product code)"
                                        />
                                    </div>

                                    <div className="import-field">
                                        <label>Supplier</label>
                                        <SupplierCombobox
                                            value={hardwareForm.supplier}
                                            onChange={(val) => setHardwareForm({ ...hardwareForm, supplier: val })}
                                            suggestions={supplierNames}
                                            placeholder="Select or type a supplier"
                                            hint="Optional — Rothoblaas / peer supplier for price history"
                                        />
                                    </div>

                                    <div className="import-field">
                                        <label>Description</label>
                                        <input
                                            type="text"
                                            value={hardwareForm.description}
                                            onChange={(e) => setHardwareForm({ ...hardwareForm, description: e.target.value })}
                                            placeholder="e.g., 6 inch, pack of 50"
                                        />
                                    </div>

                                    <div className="import-field">
                                        <label>Unit Price ($) *</label>
                                        <input
                                            type="number"
                                            step="0.01"
                                            value={hardwareForm.unitPrice}
                                            onChange={(e) => setHardwareForm({ ...hardwareForm, unitPrice: parseFloat(e.target.value) || 0 })}
                                            placeholder="e.g., 49.99"
                                        />
                                    </div>

                                    <div className="import-field">
                                        <label>Unit *</label>
                                        <select
                                            value={hardwareForm.unit}
                                            onChange={(e) => setHardwareForm({ ...hardwareForm, unit: e.target.value })}
                                            className="import-select"
                                        >
                                            <option value="each">Each</option>
                                            <option value="pack">Pack</option>
                                            <option value="box">Box</option>
                                            <option value="sq ft">Sq Ft</option>
                                            <option value="linear ft">Linear Ft</option>
                                            <option value="set">Set</option>
                                        </select>
                                    </div>

                                    {hardwareForm.category === 'Decking' && (
                                        <div className="import-field">
                                            <label>Board Width (inches) *</label>
                                            <input
                                                type="number"
                                                step="0.25"
                                                min="0"
                                                value={hardwareForm.widthInches ?? ''}
                                                onChange={(e) => setHardwareForm({ ...hardwareForm, widthInches: parseFloat(e.target.value) || undefined })}
                                                placeholder="e.g., 5.5"
                                            />
                                        </div>
                                    )}
                                </>
                            )}
                        </div>

                        <div className="import-modal-footer">
                            <button className="materials-action-btn secondary" onClick={() => setShowItemModal(false)}>
                                Cancel
                            </button>
                            <button
                                className="materials-action-btn primary"
                                onClick={handleSaveItem}
                                disabled={isWoodTab
                                    ? (!itemForm.species || !itemForm.dimensions)
                                    : (!hardwareForm.name || !hardwareForm.unitPrice)
                                }
                            >
                                {activeTab === 'glulam'
                                    ? (editingGlulamItem ? 'Save Changes' : 'Add Glulam Item')
                                    : activeTab === 'timber'
                                        ? (editingItem ? 'Save Changes' : 'Add Timber Item')
                                        : (editingHardwareItem ? 'Save Changes' : 'Add Hardware Item')
                                }
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Bulk Edit Modal */}
            {showBulkEditModal && (
                <div className="import-modal-overlay" onClick={() => setShowBulkEditModal(false)}>
                    <div className="import-modal" onClick={(e) => e.stopPropagation()}>
                        <div className="import-modal-header">
                            <h2><Edit2 size={24} /> Bulk Edit ({selectedIds.size} items)</h2>
                            <button className="import-modal-close" onClick={() => setShowBulkEditModal(false)}>
                                <X size={20} />
                            </button>
                        </div>

                        <div className="import-modal-body">
                            <p className="bulk-edit-hint">Leave fields blank to keep current values</p>

                            <div className="import-field">
                                <label>Species Name</label>
                                <input
                                    type="text"
                                    value={bulkEditSpecies}
                                    onChange={(e) => setBulkEditSpecies(e.target.value)}
                                    placeholder="e.g., White Oak, Douglas Fir"
                                />
                            </div>

                            <div className="import-field">
                                <label>Grade</label>
                                <select
                                    value={bulkEditGrade}
                                    onChange={(e) => setBulkEditGrade(e.target.value)}
                                    className="import-select"
                                >
                                    <option value="">-- Keep current --</option>
                                    {(activeTab === 'glulam' ? GLULAM_GRADE_OPTIONS : GRADE_OPTIONS).map((grade) => (
                                        <option key={grade} value={grade}>{grade}</option>
                                    ))}
                                </select>
                            </div>

                            {activeTab === 'timber' && (
                                <div className="import-field">
                                    <label>Dryness</label>
                                    <select
                                        value={bulkEditMoisture}
                                        onChange={(e) => setBulkEditMoisture(e.target.value)}
                                        className="import-select"
                                    >
                                        <option value="">-- Keep current --</option>
                                        <option value="unspecified">Unspecified</option>
                                        <option value="green">Green</option>
                                        <option value="kiln_dried">Kiln dried</option>
                                    </select>
                                    <span className="field-hint">Green vs kiln dried (matches proposal pricing)</span>
                                </div>
                            )}

                            <div className="import-field">
                                <label>Cost per Board Foot ($)</label>
                                <input
                                    type="number"
                                    step="0.01"
                                    value={bulkEditCostPerBdFt}
                                    onChange={(e) => setBulkEditCostPerBdFt(e.target.value)}
                                    placeholder="e.g., 15.00"
                                />
                            </div>

                            <div className="import-field">
                                <label>Supplier</label>
                                <SupplierCombobox
                                    value={bulkEditSupplier}
                                    onChange={setBulkEditSupplier}
                                    suggestions={supplierNames}
                                    placeholder="Select or type a supplier"
                                    hint="Existing suppliers shown as suggestions"
                                />
                            </div>
                        </div>

                        <div className="import-modal-footer">
                            <button className="materials-action-btn secondary" onClick={() => setShowBulkEditModal(false)}>
                                Cancel
                            </button>
                            <button
                                className="materials-action-btn primary"
                                onClick={handleBulkEditConfirm}
                                disabled={bulkUpdating || (!bulkEditSpecies && !bulkEditGrade && !bulkEditCostPerBdFt && !bulkEditSupplier && !bulkEditMoisture)}
                            >
                                {bulkUpdating ? 'Updating...' : 'Apply Changes'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            <SupplierImportModal
                isOpen={showSupplierImportModal}
                onClose={() => setShowSupplierImportModal(false)}
                existingItems={timberItems}
                supplierNames={supplierNames}
            />
            <TimberReport
                isOpen={showTimberReport}
                onClose={() => setShowTimberReport(false)}
                items={timberItems}
            />
            <MaterialsCatalogSnapshotsModal
                isOpen={showSnapshotsModal}
                onClose={() => setShowSnapshotsModal(false)}
            />
        </div>
    );
};

export default MaterialsDatabasePage;
